import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { type Alerta, ESTADO_META } from '../api/seguridad';

type Props = {
  alertas: Alerta[];
  selectedId: number | null;
  onSelect: (id: number) => void;
};

function coords(a: Alerta): [number, number] | null {
  const lat = a.latitud != null ? parseFloat(a.latitud) : NaN;
  const lng = a.longitud != null ? parseFloat(a.longitud) : NaN;
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return [lat, lng];
}

function iconFor(a: Alerta, selected: boolean): L.DivIcon {
  const meta = ESTADO_META[a.estado] ?? ESTADO_META.pendiente;
  const pulse = a.estado === 'pendiente';
  const size = selected ? 26 : 18;
  const ring = selected ? `box-shadow:0 0 0 4px ${meta.dot}55;` : '';
  return L.divIcon({
    className: '',
    html: `<div class="seg-pin${pulse ? ' seg-pin-pulse' : ''}" style="width:${size}px;height:${size}px;background:${meta.dot};${ring}"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

export default function MapaAlertas({ alertas, selectedId, onSelect }: Props) {
  const divRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const didFit = useRef(false);
  // Copia siempre-fresca de las alertas para el flyTo, SIN meterlas en las deps
  // del efecto (si no, cada polling re-disparaba el vuelo = el mapa "temblaba").
  const alertasRef = useRef(alertas);
  alertasRef.current = alertas;

  // Init map una vez
  useEffect(() => {
    if (!divRef.current || mapRef.current) return;
    const map = L.map(divRef.current, { zoomControl: true, attributionControl: false })
      .setView([-39.067, -67.235], 14);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    // Leaflet a veces necesita recalcular tamaño cuando el contenedor cambia
    setTimeout(() => map.invalidateSize(), 100);
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Redibujar marcadores cuando cambian las alertas o la selección
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();

    const pts: [number, number][] = [];
    for (const a of alertas) {
      const c = coords(a);
      if (!c) continue;
      pts.push(c);
      const m = L.marker(c, { icon: iconFor(a, a.id === selectedId) });
      m.on('click', () => onSelect(a.id));
      m.addTo(layer);
    }

    // Encuadrar solo la primera vez que hay puntos
    if (!didFit.current && pts.length) {
      map.fitBounds(L.latLngBounds(pts).pad(0.2), { maxZoom: 16 });
      didFit.current = true;
    }
  }, [alertas, selectedId, onSelect]);

  // Volar a la alerta SOLO cuando el usuario la selecciona (cambia selectedId).
  // Deps = [selectedId] únicamente → no se re-dispara en cada polling.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || selectedId == null) return;
    const a = alertasRef.current.find((x) => x.id === selectedId);
    const c = a ? coords(a) : null;
    if (c) map.flyTo(c, Math.max(map.getZoom(), 16), { duration: 0.6 });
  }, [selectedId]);

  return <div ref={divRef} className="w-full h-full" style={{ background: '#0b1220' }} />;
}
